Theme.Activity = (() => {
  const storageKey = 'db-activity';
  const limits = { products: 20, collections: 10, searches: 10, adds: 20 };
  const empty = () => ({ products: [], collections: [], searches: [], adds: [] });

  function read() {
    const activity = empty();
    let stored;

    try {
      stored = JSON.parse(localStorage.getItem(storageKey));
    } catch (error) {
      return activity;
    }

    Object.keys(activity).forEach((list) => {
      if (Array.isArray(stored?.[list])) activity[list] = stored[list].filter((item) => item && typeof item === 'object');
    });

    return activity;
  }

  function write(activity) {
    try {
      localStorage.setItem(storageKey, JSON.stringify(activity));
    } catch (error) {
      return;
    }
  }

  function push(list, key, entry) {
    const activity = read();
    const value = entry[key];

    activity[list] = [entry, ...activity[list].filter((item) => item[key] !== value)].slice(0, limits[list]);
    write(activity);
  }

  function recordContext() {
    const element = document.querySelector('[data-activity-context]');
    if (!element) return;

    let context;

    try {
      context = JSON.parse(element.textContent);
    } catch (error) {
      return;
    }

    const at = Date.now();

    if (context.product) push('products', 'handle', Object.assign({ at }, context.product));
    if (context.collection) push('collections', 'handle', { handle: context.collection, at });
    if (context.search) recordSearch(context.search);
  }

  function recordSearch(terms) {
    const value = String(terms || '').trim().toLowerCase();
    if (value) push('searches', 'terms', { terms: value, at: Date.now() });
  }

  function recordAdd(event) {
    const detail = event.detail || {};
    const items = Array.isArray(detail.items) ? detail.items : [detail];

    items
      .filter((item) => item && item.handle)
      .forEach((item) => push('adds', 'handle', { handle: item.handle, id: item.product_id, at: Date.now() }));
  }

  function recentProducts({ exclude = [], limit = limits.products } = {}) {
    const excluded = new Set(exclude);

    return read().products
      .filter((product) => !excluded.has(product.handle))
      .slice(0, limit);
  }

  document.addEventListener('DOMContentLoaded', recordContext);
  document.addEventListener('addtocart', recordAdd);

  return { read, recordSearch, recentProducts };
})();

class ActivityProducts extends HTMLElement {
  static cache = new Map();

  connectedCallback() {
    this.list = this.querySelector('[data-activity-products-list]');
    if (!this.list || this.hasAttribute('data-disabled')) return;

    const whenClass = this.dataset.whenClass;
    if (whenClass && !document.documentElement.classList.contains(whenClass)) return;

    const loadOn = this.dataset.loadOn;

    if (loadOn && !this.closest('.state--active')) {
      document.addEventListener(loadOn, () => this.load(), { once: true });
    } else {
      this.load();
    }
  }

  load() {
    if (!this.isConnected || this.loaded) return;
    this.loaded = true;

    const limit = parseInt(this.dataset.limit, 10) || 5;
    const lead = parseInt(this.dataset.lead, 10) || limit;
    const exclude = (this.dataset.exclude || '').split(',').filter(Boolean);
    const candidates = Theme.Activity.recentProducts({ exclude, limit: lead * 2 });

    if (candidates.length) this.render(candidates, limit, lead);
  }

  fetchSection(handle) {
    const root = window.Shopify?.routes?.root || '/';
    const url = `${root}products/${encodeURIComponent(handle)}?section_id=${this.dataset.section}`;

    if (!ActivityProducts.cache.has(url)) {
      ActivityProducts.cache.set(url, fetch(url)
        .then((response) => (response.ok ? response.text() : ''))
        .catch(() => ''));
    }

    return ActivityProducts.cache.get(url);
  }

  render(candidates, limit, lead = limit) {
    const itemSelector = this.dataset.itemSelector || '[data-activity-item]';
    const requests = candidates.map((product) => this.fetchSection(product.handle));

    return Promise.all(requests).then((responses) => {
      const items = responses
        .map((markup) => new DOMParser().parseFromString(markup, 'text/html').querySelector(itemSelector))
        .filter((item) => item && item.dataset.handle)
        .slice(0, lead)
        .map((item) => document.importNode(item, true));

      if (!items.length) return;

      if (this.dataset.mode === 'prepend') {
        const handles = new Set(items.map((item) => item.dataset.handle));

        this.list.querySelectorAll(':scope > [data-handle]').forEach((item) => {
          if (handles.has(item.dataset.handle)) item.remove();
        });

        this.list.prepend(...items);
        [...this.list.children].slice(limit).forEach((item) => item.remove());
      } else {
        this.list.replaceChildren(...items);
      }

      const heading = this.querySelector('[data-activity-heading]');
      if (heading && this.dataset.heading) heading.textContent = this.dataset.heading;

      this.hidden = false;
      this.dispatchEvent(new CustomEvent('activityproducts:rendered', { bubbles: true }));

      if (typeof alignProductCardsHeight === 'function') alignProductCardsHeight(this);
    });
  }
}

customElements.define('activity-products', ActivityProducts);
